/* global jQuery */
'use strict';

const localStorageSlim = require('localstorage-slim');
const utils = require('public/js/helpers/utils');

const CSRF_TOKEN_ENDPOINT = '/ajax/csrf/token';
const CSRF_TOKEN_SCOPE_CACHE_KEY = 'csrf_token_scope';
const CSRF_TOKEN_CACHE_TTL_SECONDS = 300;
const CSRF_TOKEN_CACHE_KEY_PREFIX = 'csrf_token';

/**
 * Returns the session scope used for CSRF cache partitioning.
 * @returns {string}
 */
function getSessionScope() {
	const runtimeScope = globalThis?.rp_app?.data?.csrf_session_scope;
	if (typeof runtimeScope === 'string' && runtimeScope) {
		return runtimeScope;
	}

	const cachedScope = localStorageSlim.get(CSRF_TOKEN_SCOPE_CACHE_KEY);
	if (typeof cachedScope === 'string' && cachedScope) {
		return cachedScope;
	}

	return '';
}

/**
 * Returns the localStorage key for the current CSRF token cache scope.
 * @param {string} sessionScope
 * @returns {string}
 */
function getCsrfTokenCacheKey(sessionScope = getSessionScope()) {
	const scope = typeof sessionScope === 'string' && sessionScope ? sessionScope : 'anonymous';
	return `${CSRF_TOKEN_CACHE_KEY_PREFIX}:${scope}`;
}

/**
 * Persists CSRF session scope in memory and localStorage.
 * @param {string} sessionScope
 * @returns {void}
 */
function setCsrfSessionScope(sessionScope) {
	if (typeof sessionScope !== 'string' || !sessionScope) {
		return;
	}

	globalThis.rp_app = globalThis.rp_app || {};
	globalThis.rp_app.data = globalThis.rp_app.data || {};
	globalThis.rp_app.data.csrf_session_scope = sessionScope;
	localStorageSlim.set(CSRF_TOKEN_SCOPE_CACHE_KEY, sessionScope, { ttl: CSRF_TOKEN_CACHE_TTL_SECONDS });
}

/**
 * Determines whether a localStorage key belongs to CSRF token cache entries.
 * @param {unknown} key
 * @returns {boolean}
 */
function isCsrfTokenCacheKey(key) {
	return typeof key === 'string' && key.startsWith(`${CSRF_TOKEN_CACHE_KEY_PREFIX}:`);
}

/**
 * Prunes expired CSRF token cache entries.
 * localstorage-slim auto-removes expired keys on `.get()`, so reading each
 * CSRF key is sufficient to trigger cleanup.
 *
 * @returns {void}
 */
function pruneExpiredCsrfTokenCacheEntries() {
	if (!globalThis?.localStorage || typeof globalThis.localStorage.length !== 'number') {
		return;
	}

	for (let index = globalThis.localStorage.length - 1; index >= 0; index -= 1) {
		const key = globalThis.localStorage.key(index);
		if (isCsrfTokenCacheKey(key)) {
			localStorageSlim.get(key);
		}
	}

	localStorageSlim.get(CSRF_TOKEN_SCOPE_CACHE_KEY);
}

/**
 * Returns plugin JWT headers when available.
 * @returns {Record<string, string>}
 */
function getPluginJwtHeaders() {
	const jwt = globalThis?.rp_app?.jwt;
	if (typeof jwt !== 'string' || !jwt) {
		return {};
	}

	return {
		'X-RP-JWT': jwt,
	};
}

/**
 * Returns cached CSRF token metadata when available.
 * @returns {{ token: string, fromCache: boolean }|null}
 */
function getCachedTokenResult() {
	const sessionScope = getSessionScope();
	if (!sessionScope) {
		return null;
	}

	const existingToken = localStorageSlim.get(getCsrfTokenCacheKey(sessionScope));
	if (!existingToken) {
		return null;
	}

	return buildTokenResult(existingToken, true, sessionScope);
}

/**
 * Builds normalized token result payload.
 * @param {unknown} rawToken
 * @param {boolean} fromCache
 * @param {unknown} rawSessionScope
 * @returns {{ token: string, fromCache: boolean, sessionScope: string }}
 */
function buildTokenResult(rawToken, fromCache, rawSessionScope) {
	return {
		token: typeof rawToken === 'string' ? rawToken : '',
		fromCache,
		sessionScope: typeof rawSessionScope === 'string' ? rawSessionScope : '',
	};
}

/**
 * Builds a non-cached token result from API payload data.
 * @param {unknown} data
 * @returns {{ token: string, fromCache: boolean, sessionScope: string }}
 */
function buildFetchedTokenResult(data) {
	const token = typeof data?.token === 'string' ? data.token : '';
	const sessionScope = typeof data?.session_scope === 'string' ? data.session_scope : '';
	if (!token) {
		throw new Error('csrf_token_fetch_failed');
	}
	if (!sessionScope) {
		throw new Error('csrf_scope_fetch_failed');
	}

	return buildTokenResult(token, false, sessionScope);
}

/**
 * Fetches a CSRF token via jQuery.ajax and returns token metadata.
 * @returns {Promise<{ token: string, fromCache: boolean }>}
 */
function fetchTokenViaJquery() {
	if (utils.isBot()) {
		return Promise.resolve(buildTokenResult('', false, ''));
	}

	const cachedTokenResult = getCachedTokenResult();
	if (cachedTokenResult) {
		return Promise.resolve(cachedTokenResult);
	}

	if (typeof jQuery === 'undefined' || typeof jQuery.ajax !== 'function') {
		return fetchTokenViaHttp();
	}

	return new Promise((resolve, reject) => {
		jQuery.ajax({
			url: CSRF_TOKEN_ENDPOINT,
			type: 'GET',
			dataType: 'json',
			cache: false,
			headers: getPluginJwtHeaders(),
			success: (data) => {
				try {
					resolve(buildFetchedTokenResult(data));
				} catch (error) {
					reject(error);
				}
			},
			error: () => reject(new Error('csrf_token_fetch_failed')),
		});
	});
}

/**
 * Fetches a CSRF token using the Fetch API when jQuery is unavailable.
 * @returns {Promise<{ token: string, fromCache: boolean }>}
 */
function fetchTokenViaHttp() {
	return fetch(CSRF_TOKEN_ENDPOINT, {
		method: 'GET',
		credentials: 'same-origin',
		cache: 'no-store',
		headers: {
			Accept: 'application/json',
			...getPluginJwtHeaders(),
		},
	})
		.then((response) => {
			if (!response || !response.ok || typeof response.json !== 'function') {
				throw new Error('csrf_token_fetch_failed');
			}
			return response.json();
		})
		.then((data) => buildFetchedTokenResult(data));
}

module.exports = {
	CSRF_TOKEN_CACHE_KEY_PREFIX,
	CSRF_TOKEN_CACHE_TTL_SECONDS,
	CSRF_TOKEN_ENDPOINT,
	CSRF_TOKEN_SCOPE_CACHE_KEY,
	fetchTokenViaJquery,
	getCsrfTokenCacheKey,
	isCsrfTokenCacheKey,
	pruneExpiredCsrfTokenCacheEntries,
	setCsrfSessionScope,
};
